import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { CaretLeft, SignOut } from '@phosphor-icons/react';
import { css } from 'styled-system/css';
import type { Position, Puzzle } from '@/domain';
import type { GameEvent, Unsubscribe } from '@/application/game';
import { tallyValidatedLetters } from '@/application/game';
import type { Player, PlayerId, SessionId } from '@/domain/game';
import type { SoundPlayer } from '@/application/session/SoundPlayer';
import type { SoundStore } from '@/application/session/SoundStore';
import type { SkipFilledStore } from '@/application/session/SkipFilledStore';
import type { SurveyClient } from '@/application/survey';
import { ClueRail, Lockup } from '@/design-system';
import { t } from '@/ui/i18n';
import { AppShell } from '@/ui/v2/AppShell';
import { DesktopAppBar } from '@/ui/v2/DesktopAppBar';
import { useGridNavigation } from '@/ui/components/grid/useGridNavigation';
import { orderClues } from '@/ui/components/grid/orderClues';
import { CELL, GAP, BOARD_BOTTOM_GAP, posKey } from '@/ui/components/grid/playLayout';
import { PuzzleBoard, type PuzzleBoardHandle } from '@/ui/components/grid/PuzzleBoard';
import { useAdvanceOnValidation } from '@/ui/components/grid/useAdvanceOnValidation';
import { ReportClueSheet } from '@/ui/components/grid/ReportClueSheet';
import { Keyboard } from '@/ui/play/Keyboard';
import { useGridSounds } from '@/ui/play/useGridSounds';
import { GridSoundToggle } from '@/ui/play/GridSoundToggle';
import { usePresenceState } from '@/ui/components/grid/usePresenceState';
import { useAnnouncer } from '@/ui/components/a11y/Announcer';
import { useCoopValidating } from './useCoopValidating';
import { CoopPresenceLayer } from './CoopPresenceLayer';
import { LiveTimer } from './LiveTimer';
import { PlayerStrip } from './PlayerStrip';
import { useIsDesktop } from '@/ui/lib/useIsDesktop';

// ADR-0072 v2 co-op IN_PROGRESS screen: shared grid + presence + timer + roster, wired like prod InGameView.

const GUTTER = '14px';
const header = css({ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 4, padding: `12px ${GUTTER} 0`, display: 'flex', flexDirection: 'column', gap: '8px', lg: { position: 'static', width: '100%', paddingTop: '24px', paddingInline: '36px' } });
// Desktop presence row: aligned with the contained board under the shared nav bar.
const coopPresence = css({ display: 'none', lg: { display: 'block', width: '100%', maxWidth: '760px', marginInline: 'auto', paddingInline: '14px', paddingTop: '10px' } });
const headerBar = css({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  bg: 'ws.glass',
  backdropFilter: 'blur(10px)',
  border: '0.5px solid token(colors.ws.glassBorder)',
  borderRadius: '999px',
  padding: '5px 8px',
  boxShadow: '0 2px 12px rgba(33,75,64,0.14)',
  // Desktop: a plain transparent bar (matching the home top bar), not a frosted floating pill.
  lg: { bg: 'transparent', backdropFilter: 'none', border: 'none', borderRadius: 0, boxShadow: 'none', padding: '0', gap: '14px' },
});
const iconBtn = css({
  width: '32px',
  height: '32px',
  borderRadius: '50%',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: 'none',
  background: 'transparent',
  color: 'ws.jadeInk',
  fontSize: '18px',
  cursor: 'pointer',
  flex: 'none',
  _active: { background: 'rgba(33,75,64,0.08)' },
  _focusVisible: { outline: '3px solid token(colors.ws.sakuraRose)', outlineOffset: '2px' },
  // Desktop: frosted circle button matching the home top bar's menu button.
  lg: { width: '44px', height: '44px', background: 'ws.glass', boxShadow: '0 1px 2px rgba(33,75,64,0.08)', fontSize: '20px', _hover: { background: 'ws.glassHover' } },
});
const headerSpacer = css({ flex: 1 });
// ADR-0098 §6: claim affordance for an ownerless game, non-invasive banner.
const claimBanner = css({ position: 'absolute', top: '64px', left: 0, right: 0, zIndex: 5, display: 'flex', justifyContent: 'center', paddingInline: GUTTER, lg: { position: 'static', paddingTop: '8px' } });
const claimPill = css({ display: 'inline-flex', alignItems: 'center', gap: '10px', bg: 'ws.card', border: '0.5px solid token(colors.ws.glassBorder)', borderRadius: '999px', padding: '6px 8px 6px 14px', boxShadow: '0 2px 12px rgba(33,75,64,0.16)', fontFamily: 'wsUi', fontSize: '13px', fontWeight: 'bold', color: 'ws.jadeInk' });
const claimBtn = css({ display: 'inline-flex', alignItems: 'center', gap: '6px', border: 'none', borderRadius: '999px', bg: 'ws.jadeInk', color: 'ws.onJadeInk', fontFamily: 'wsUi', fontWeight: 'black', fontSize: '13px', padding: '7px 14px', cursor: 'pointer', _disabled: { opacity: 0.6, cursor: 'default' }, _focusVisible: { outline: '3px solid token(colors.ws.sakuraRose)', outlineOffset: '2px' } });
const viewportFill = css({ flex: '1', minHeight: 0, lg: { width: '100%' } });

const bottomBar = css({ position: 'absolute', left: 0, right: 0, bottom: 0, zIndex: 4, display: 'flex', flexDirection: 'column', gap: '10px', padding: `8px ${GUTTER} 14px`, md: { alignItems: 'center', '& > *': { width: '100%', maxWidth: '520px' } }, lg: { position: 'static', paddingBottom: '24px' } });

export interface LiveCoopScreenProps {
  readonly puzzle: Puzzle;
  readonly startedAt: string;
  // Authoritative duration once COMPLETED; freezes the timer and lights the grid.
  readonly frozenAtMs?: number;
  readonly isCompleted: boolean;
  readonly sessionId: SessionId;
  // ADR-0066 (e): account-scoped local identity for roster "you"/score/colour; `sessionId` stays for the cell-sync transport + presence.
  readonly currentPlayerId: PlayerId;
  readonly players: ReadonlyArray<Player>;
  readonly playersBySessionId: ReadonlyMap<SessionId, Player>;
  readonly initialEntries: ReadonlyArray<{ row: number; column: number; letter: string }>;
  readonly lockedPositions: ReadonlyArray<{ row: number; column: number; lockedBy: PlayerId }>;
  readonly onCellChange: (row: number, col: number, letter: string | null) => void;
  readonly onLocalFocusChange: (position: Position | null, direction: 'across' | 'down' | null) => void;
  readonly subscribeToRemoteCellUpdates: (handler: (event: GameEvent) => void) => Unsubscribe;
  readonly subscribeToRemotePresence: (handler: (event: GameEvent) => void) => Unsubscribe;
  readonly onLeave: () => void;
  // ADR-0098 §6: snapshot-authoritative ownerless flag (drives the claim banner on reload/re-entry); live `ownershipChanged` is applied on top.
  readonly ownerless?: boolean;
  // ADR-0098 §6: claim ownership of a now-ownerless game.
  readonly onClaim?: () => Promise<void>;
  readonly soundPlayer?: SoundPlayer;
  readonly soundStore?: SoundStore;
  readonly skipFilledStore?: SkipFilledStore;
  readonly surveyClient?: SurveyClient;
}

export function LiveCoopScreen({
  puzzle,
  startedAt,
  frozenAtMs,
  isCompleted,
  sessionId,
  currentPlayerId,
  players,
  playersBySessionId,
  initialEntries,
  lockedPositions,
  onCellChange,
  onLocalFocusChange,
  subscribeToRemoteCellUpdates,
  subscribeToRemotePresence,
  onLeave,
  ownerless = false,
  onClaim,
  soundPlayer,
  soundStore,
  skipFilledStore,
  surveyClient,
}: LiveCoopScreenProps) {
  const boardRef = useRef<PuzzleBoardHandle>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const [bottomInset, setBottomInset] = useState(220);
  useEffect(() => {
    const el = bottomRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => setBottomInset(el.offsetHeight));
    setBottomInset(el.offsetHeight);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const isDesktop = useIsDesktop();

  const entryAt = useMemo(() => {
    const m = new Map<string, string>();
    for (const e of initialEntries) m.set(posKey(e.row, e.column), e.letter);
    return m;
  }, [initialEntries]);

  // Server-locked cells (per-word `wordLocked`); COMPLETED lights every letter.
  const validatedPositions = useMemo<ReadonlySet<string>>(() => {
    const set = new Set<string>();
    for (const p of lockedPositions) set.add(posKey(p.row, p.column));
    if (isCompleted) {
      for (const c of puzzle.cells) {
        if (c.kind === 'letter') set.add(posKey(c.position.row, c.position.col));
      }
    }
    return set;
  }, [isCompleted, lockedPositions, puzzle.cells]);

  // ADR-0086 / ADR-0066 (e): per-cell finder (account-scoped `playerId`) so PuzzleBoard tints each locked cell with the player's colour.
  const lockedByAt = useMemo<ReadonlyMap<string, PlayerId>>(() => {
    const m = new Map<string, PlayerId>();
    for (const p of lockedPositions) m.set(posKey(p.row, p.column), p.lockedBy);
    return m;
  }, [lockedPositions]);

  const scoresByPlayerId = useMemo(() => tallyValidatedLetters(lockedPositions), [lockedPositions]);

  const validatedRef = useRef(validatedPositions);
  validatedRef.current = validatedPositions;

  // Word pulses as cells lock (any player's); the win cue fires at the lobby route (this screen unmounts into Résultats).
  useGridSounds({ validatedCount: validatedPositions.size, won: false, soundPlayer });

  // Discreet "checking…" pulse on a word the local player just completed, until the server locks it (or a safety window passes).
  const { validating, rejecting, noteLocalFill, noteServerReject } = useCoopValidating(puzzle, validatedPositions);
  // Cells written locally but not yet echoed back by the server — lets the rejoin replay resync (below) tell a stale pre-flush snapshot apart from a confirmed value.
  const unconfirmedLocalWritesRef = useRef(new Map<string, string | null>());
  const handleLocalCellChange = useCallback(
    (row: number, col: number, letter: string | null) => {
      unconfirmedLocalWritesRef.current.set(posKey(row, col), letter);
      onCellChange(row, col, letter);
      if (letter) noteLocalFill(row, col);
    },
    [onCellChange, noteLocalFill],
  );

  const nav = useGridNavigation(puzzle, {
    onCellChange: isCompleted ? undefined : handleLocalCellChange,
    onFocusChange: isCompleted
      ? undefined
      : (position, direction) => {
          boardRef.current?.cancelBeat();
          onLocalFocusChange(position, direction);
        },
    // Suppress the hook's keyboard-avoidance auto-scroll — the grid never moves the view on its own.
    getZoomScale: () => 2,
    isCellValidated: (row, col) => validatedRef.current.has(posKey(row, col)),
    skipFilledOnAdvance: () => skipFilledStore?.load() ?? true,
  });

  // Inbound remote writes land directly on the uncontrolled inputs (ADR-0002 §4), never re-emitting onCellChange.
  const { applyRemoteCellUpdate } = nav;
  // Rejoin replay resync (skips a cell with an unconfirmed local write — the snapshot can predate the post-reconnect flush and would otherwise revert it until the echo below lands).
  useEffect(() => {
    for (const e of initialEntries) {
      const key = posKey(e.row, e.column);
      const pending = unconfirmedLocalWritesRef.current;
      if (pending.has(key)) {
        if (pending.get(key) !== e.letter) continue;
        pending.delete(key);
      }
      applyRemoteCellUpdate(e.row, e.column, e.letter);
    }
  }, [initialEntries, applyRemoteCellUpdate]);
  // ADR-0085: the same raw stream carries `wordRejected` — shake synchronously off the server verdict.
  useEffect(() => {
    const unsubscribe = subscribeToRemoteCellUpdates((event) => {
      if (event.type === 'cellUpdated') {
        if (event.sessionId === sessionId) unconfirmedLocalWritesRef.current.delete(posKey(event.row, event.column));
        applyRemoteCellUpdate(event.row, event.column, event.letter);
      } else if (event.type === 'wordRejected') noteServerReject(event.positions);
    });
    return unsubscribe;
  }, [subscribeToRemoteCellUpdates, applyRemoteCellUpdate, noteServerReject, sessionId]);

  // ADR-0098 §6: ownerless is snapshot-authoritative (works on reload/re-entry); a live `ownershipChanged` frame then overrides it (`undefined` = no live signal yet ⇒ fall back to the snapshot).
  const [liveOwnerUserId, setLiveOwnerUserId] = useState<string | null | undefined>(undefined);
  const [claiming, setClaiming] = useState(false);
  useEffect(() => {
    const unsubscribe = subscribeToRemoteCellUpdates((event) => {
      if (event.type === 'ownershipChanged') setLiveOwnerUserId(event.newOwnerUserId);
    });
    return unsubscribe;
  }, [subscribeToRemoteCellUpdates]);
  const isOwnerless = liveOwnerUserId !== undefined ? liveOwnerUserId === null : ownerless;
  const handleClaim = useCallback(() => {
    if (!onClaim || claiming) return;
    setClaiming(true);
    // ADR-0098 §6: success broadcasts `ownershipChanged`, flipping `isOwnerless` false and hiding this button.
    onClaim().finally(() => setClaiming(false));
  }, [onClaim, claiming]);

  // Peer presence state → typing/idle/lost sets for the roster strip + grid badges.
  const presenceState = usePresenceState(subscribeToRemotePresence, sessionId);
  const typingSessionIds = useMemo(() => {
    const set = new Set<SessionId>();
    for (const [sid, st] of presenceState) if (st.typing) set.add(sid);
    return set;
  }, [presenceState]);
  const idleSessionIds = useMemo(() => {
    const set = new Set<SessionId>();
    for (const [sid, st] of presenceState) if (st.idle) set.add(sid);
    return set;
  }, [presenceState]);
  const disconnectingSessionIds = useMemo(() => {
    const set = new Set<SessionId>();
    for (const [sid, st] of presenceState) if (st.connectionLost) set.add(sid);
    return set;
  }, [presenceState]);

  // ADR-0050: announce completion once (join/leave + mot validé already announced inside useLobbyConnection).
  const announcer = useAnnouncer();
  const announcedSolvedRef = useRef(false);
  useEffect(() => {
    if (isCompleted && !announcedSolvedRef.current) {
      announcedSolvedRef.current = true;
      announcer.say(t('v2.multiplayer.coop.sr.solved'));
    }
  }, [isCompleted, announcer]);

  // ADR-0098 §6 / ADR-0050: the claim banner is a visual overlay, so announce the ownerless transition for screen readers.
  useEffect(() => {
    if (isOwnerless) announcer.say(t('v2.multiplayer.coop.ownerless'));
  }, [isOwnerless, announcer]);

  const clue = nav.currentClue;
  // Shared focus-advance firewall: a server `wordLocked` advances the cursor to the next word, same as solo.
  const advance = useAdvanceOnValidation({ puzzle, nav, validatedPositions, currentClue: clue, completed: isCompleted });
  const orderedClues = useMemo(() => orderClues(puzzle), [puzzle]);
  // Clue ordinal among all across-then-down clues — drives the rail counter, same as solo PlayScreen.
  const clueOrdinal = useMemo(() => {
    if (!clue) return -1;
    const k = `${clue.definition.position.row}:${clue.definition.position.col}:${clue.clue.arrow}`;
    return orderedClues.findIndex((c) => c.key === k);
  }, [clue, orderedClues]);

  // Rail stays mounted from init (before any focus) so the board fit reserves its height and never shifts when it later appears — same sticky-fallback as solo PlayScreen. Shows the live clue on focus, the last clue on blur, the first unfilled clue on load.
  const [lastOrdinal, setLastOrdinal] = useState(() => {
    const i = orderedClues.findIndex((c) => c.cells.some((p) => !entryAt.has(posKey(p.row, p.col))));
    return i < 0 ? 0 : i;
  });
  useEffect(() => {
    if (clueOrdinal >= 0) setLastOrdinal(clueOrdinal);
  }, [clueOrdinal]);
  const displayOrdinal = clueOrdinal >= 0 ? clueOrdinal : lastOrdinal;
  const displayClue = orderedClues[displayOrdinal];

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Tab') advance.markJump(e.shiftKey ? -1 : 1);
      nav.handleKeyDown(e);
    },
    [nav, advance],
  );

  return (
    <AppShell
      variant="overlay"
      desktopBar={
        isDesktop ? (
          <>
            <DesktopAppBar
              trailing={
                <>
                  <LiveTimer startedAt={startedAt} frozenAtMs={frozenAtMs} />
                  {soundStore ? <GridSoundToggle soundStore={soundStore} className={iconBtn} /> : null}
                </>
              }
            />
            <div className={coopPresence}>
              <PlayerStrip
                players={players}
                currentPlayerId={currentPlayerId}
                typingSessionIds={typingSessionIds}
                idleSessionIds={idleSessionIds}
                disconnectingSessionIds={disconnectingSessionIds}
                scoresByPlayerId={scoresByPlayerId}
              />
            </div>
          </>
        ) : null
      }
      topBar={
        isDesktop ? undefined : (
          <header className={header}>
            <div className={headerBar}>
              <button type="button" className={iconBtn} onClick={onLeave} aria-label={t('v2.multiplayer.coop.aria.leaveGame')}>
                <CaretLeft aria-hidden="true" weight="bold" />
              </button>
              <Lockup orientation="horizontal" tone="jade" iconSize={26} textSize={17} gap={8} />
              <span className={headerSpacer} />
              <LiveTimer startedAt={startedAt} frozenAtMs={frozenAtMs} />
              {soundStore ? <GridSoundToggle soundStore={soundStore} className={iconBtn} /> : null}
              <button type="button" className={iconBtn} onClick={onLeave} aria-label={t('v2.multiplayer.leave')}>
                <SignOut aria-hidden="true" weight="bold" />
              </button>
            </div>
            <PlayerStrip
              players={players}
              currentPlayerId={currentPlayerId}
              typingSessionIds={typingSessionIds}
              idleSessionIds={idleSessionIds}
              disconnectingSessionIds={disconnectingSessionIds}
              scoresByPlayerId={scoresByPlayerId}
            />
          </header>
        )
      }
      bottomBar={
        <div className={bottomBar} ref={bottomRef}>
          {displayClue ? (
            <ClueRail
              direction={displayClue.across ? 'horizontal' : 'vertical'}
              directionLabel={t(displayClue.across ? 'clueRail.direction.horizontal' : 'clueRail.direction.vertical')}
              clue={displayClue.text}
              letterCount={displayClue.cells.length}
              letterCountLabel={t('clueRail.letterCount', { count: displayClue.cells.length })}
              index={displayOrdinal + 1}
              total={orderedClues.length}
              groupLabel={t('clueRail.aria.group')}
              counterLabel={t('clueRail.aria.counter', { index: displayOrdinal + 1, total: orderedClues.length })}
              prevLabel={t('clueRail.aria.prev')}
              nextLabel={t('clueRail.aria.next')}
              zoomInLabel={t('clueRail.aria.zoomIn')}
              zoomOutLabel={t('clueRail.aria.zoomOut')}
              onPrev={() => nav.cycleClue(-1)}
              onNext={() => nav.cycleClue(1)}
              onZoomIn={() => boardRef.current?.panZoom?.zoomIn()}
              onZoomOut={() => boardRef.current?.panZoom?.zoomOut()}
              report={
                surveyClient ? (
                  <ReportClueSheet
                    surveyClient={surveyClient}
                    surface="multiplayer"
                    clueText={displayClue.text}
                    puzzleId={puzzle.id}
                  />
                ) : undefined
              }
            />
          ) : null}
          {!isCompleted ? (
            <Keyboard onLetter={(l) => nav.enterLetter(l)} onBackspace={() => nav.eraseLetter()} />
          ) : null}
        </div>
      }
    >
      {onClaim && isOwnerless ? (
        <div className={claimBanner}>
          <span className={claimPill}>
            {t('v2.multiplayer.coop.ownerless')}
            <button type="button" className={claimBtn} onClick={handleClaim} disabled={claiming} aria-busy={claiming || undefined}>
              {claiming ? t('v2.multiplayer.coop.claiming') : t('v2.multiplayer.coop.claim')}
            </button>
          </span>
        </div>
      ) : null}

      <PuzzleBoard
        ref={boardRef}
        puzzle={puzzle}
        nav={nav}
        validatedPositions={validatedPositions}
        lockedByAt={lockedByAt}
        validatingPositions={validating}
        rejectingPositions={rejecting}
        entryAt={entryAt}
        className={viewportFill}
        padTop={isDesktop ? 12 : 104}
        padBottom={isDesktop ? 12 : bottomInset + BOARD_BOTTOM_GAP}
        padX={14}
        edgeFade
        onKeyDown={handleKeyDown}
        onBeatComplete={advance.onBeatComplete}
        overlay={
          <CoopPresenceLayer
            puzzle={puzzle}
            subscribeToRemotePresence={subscribeToRemotePresence}
            currentSessionId={sessionId}
            playersBySessionId={playersBySessionId}
            validatedPositions={validatedPositions}
            typingSessionIds={typingSessionIds}
            cellSize={CELL}
            gap={GAP}
          />
        }
      />
    </AppShell>
  );
}
